import * as XLSX from 'xlsx';
import {
  RFQ_HEADER_FIELDS,
  RFQ_ITEM_COLUMNS,
  RfqExcelLookupData,
} from './rfq-excel.types';
import { resolveHeaderAliasKey } from './rfq-excel-aliases';

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Column widths + frozen header row so column titles stay visible in Excel. */
function formatDataSheet(
  sheet: XLSX.WorkSheet,
  columnWidths: number[],
  freezeHeaderRow: number
): void {
  sheet['!cols'] = columnWidths.map((wch) => ({ wch }));
  sheet['!views'] = [
    {
      state: 'frozen',
      ySplit: freezeHeaderRow,
      topLeftCell: `A${freezeHeaderRow + 1}`,
      activePane: 'bottomLeft',
    },
  ];
}

function headerLabel(label: string, required: boolean): string {
  return required ? `${label} *` : label;
}

/** Sample values aligned 1:1 with RFQ_HEADER_FIELDS order. */
function sampleHeaderValue(key: string): string {
  const samples: Record<string, string> = {
    quoteDeadline: '2026-06-15',
    department: 'engine',
    vesselName: 'MV ATLANTIC',
    category: 'Engine Parts',
    brand: 'Caterpillar',
    model: 'C3516',
    vendors: 'Vendor A, Vendor B',
    currency: 'USD',
    urgencyLevel: 'Normal',
    quoteValidityDays: '30',
    requisitionRef: 'REQ-2026-001',
    equipmentTags: 'MAIN ENGINE',
    subCategory: 'Piston Rings',
    estimatedTotalCost: '5000',
    remarks: 'Emergency spare parts required',
    contactName: 'John Smith',
    contactEmail: 'john@company.com',
    contactPhone: '+1 555 0100',
    contactDepartment: 'Procurement',
    deliveryRequiredAt: 'port',
    deliveryType: 'berth',
    portNameInput: 'Singapore',
    portEtaDate: '2026-06-20',
    portAgentNameInput: 'ABC Port Agency',
    leadDate: '2026-06-25',
    creditType: 'Open Credit',
    paymentTerm: 'Net 30 Days',
    insuranceTerm: "Seller's Responsibility",
    taxTerm: 'GST Extra',
    transportTerm: 'Sea Freight',
    deliveryTerm: '7–14 Days',
    packingTerm: 'Standard Packing',
    requiredPaymentTerms: '30 Days Credit',
    requiredCertificates: 'Class, OEM',
    requiredDocuments: 'Commercial Invoice, Packing List',
    packingInstructions: 'Wooden crate, moisture protection',
    partialDeliveryAllowed: 'yes',
  };
  return samples[key] ?? '';
}

/** Sample item row aligned 1:1 with RFQ_ITEM_COLUMNS order. */
export function buildRfqTemplateSampleItemRow(): string[] {
  return [
    'Cylinder Liner', // Description *
    '5', // Quantity *
    'Pcs', // UOM *
    'Genuine', // Quality
    'DWG-001', // Drawing No
    'PART-123', // Part No
    '550001', // IMPA No
    'ALT-456', // Alt Part No
    '1', // Position No
    'SN-98765', // Model / Serial
    '100x50x10', // Dimensions
    'normal', // Priority
    '2026-06-20', // Expected Delivery Date
    'Required for cylinder 1', // Remarks
  ];
}

/**
 * Official Create-RFQ import workbook.
 * Tabular sheets with frozen header rows — column titles match import aliases.
 */
export function downloadRfqExcelTemplate(lookup?: RfqExcelLookupData): void {
  const wb = XLSX.utils.book_new();

  const instructions = [
    ['RFQ Excel Import — Instructions'],
    [''],
    ['THIS FILE IS THE CREATE-RFQ IMPORT TEMPLATE (not an RFQ list export).'],
    ['Do not upload RFQ list / report workbooks (sheets like Vessels, RFQs, Summary).'],
    [''],
    ['HOW TO USE'],
    ['  1. RFQ_Header — row 1 = field titles, row 2 = your values (edit row 2 only).'],
    ['  2. RFQ_Items — row 1 = column titles, rows 2+ = one line item per row.'],
    ['  3. Optional: Field_Map shows Label → Form Key mapping used by the importer.'],
    ['  4. Optional: Reference_Data lists valid vessel / category / vendor / port names.'],
    ['  5. Save and upload on Create RFQ → Import Excel / PDF.'],
    [''],
    ['REQUIRED HEADER FIELDS'],
    ['  Quote Deadline *, Department *, Vessel Name *, Category *, Brand *, Model *, Vendors *'],
    [''],
    ['REQUIRED ITEM COLUMNS'],
    ['  Description *, Quantity *, UOM *'],
    [''],
    ['FORMATS'],
    ['  Date: YYYY-MM-DD'],
    ['  Department: engine, deck, cabin, operations, safety, other'],
    ['  UOM: Pcs, Kgs, Ltrs, Mtrs, Sets'],
    ['  Priority: normal, urgent, critical'],
    ['  Vendors: comma-separated names matching your vendor list'],
    [''],
    ['NOTES'],
    ['  • Keep sheet names RFQ_Header and RFQ_Items.'],
    ['  • Keep row-1 column titles exactly as provided (aliases are supported, but titles are safest).'],
    ['  • Attachments are not imported via Excel — upload them in the UI.'],
  ];
  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionsSheet['!cols'] = [{ wch: 110 }];
  XLSX.utils.book_append_sheet(wb, instructionsSheet, 'Instructions');

  // Tabular header: one row of titles, one row of values (horizontal layout)
  const headerTitles = RFQ_HEADER_FIELDS.map((f) => headerLabel(f.label, f.required));
  const headerValues = RFQ_HEADER_FIELDS.map((f) => {
    if (f.key === 'vendors') return sampleHeaderValue('vendors');
    if (f.key === 'portNameInput') return sampleHeaderValue('portNameInput');
    if (f.key === 'portAgentNameInput') return sampleHeaderValue('portAgentNameInput');
    return sampleHeaderValue(String(f.key));
  });
  const headerSheet = XLSX.utils.aoa_to_sheet([headerTitles, headerValues]);
  formatDataSheet(
    headerSheet,
    headerTitles.map((t) => Math.min(Math.max(t.length + 2, 14), 28)),
    1
  );
  XLSX.utils.book_append_sheet(wb, headerSheet, 'RFQ_Header');

  // Tabular items: header row + sample + blank rows
  const itemHeaders = RFQ_ITEM_COLUMNS.map((c) => headerLabel(c.header, c.required));
  const sampleItem = buildRfqTemplateSampleItemRow();
  const itemsRows = [
    itemHeaders,
    sampleItem,
    ...Array.from({ length: 8 }, () => itemHeaders.map(() => '')),
  ];
  const itemsSheet = XLSX.utils.aoa_to_sheet(itemsRows);
  formatDataSheet(
    itemsSheet,
    itemHeaders.map((h) => Math.min(Math.max(h.length + 2, 10), 28)),
    1
  );
  XLSX.utils.book_append_sheet(wb, itemsSheet, 'RFQ_Items');

  // Explicit mapping reference (Label ↔ Form key)
  const mapRows: string[][] = [
    ['Field Label (Excel column title)', 'Form Key (import mapping)', 'Required', 'Sheet'],
    ...RFQ_HEADER_FIELDS.map((f) => [
      f.label,
      String(f.key),
      f.required ? 'YES' : 'NO',
      'RFQ_Header',
    ]),
    ...RFQ_ITEM_COLUMNS.map((c) => [
      c.header,
      c.key,
      c.required ? 'YES' : 'NO',
      'RFQ_Items',
    ]),
  ];
  const mapSheet = XLSX.utils.aoa_to_sheet(mapRows);
  formatDataSheet(mapSheet, [36, 28, 10, 12], 1);
  XLSX.utils.book_append_sheet(wb, mapSheet, 'Field_Map');

  if (lookup) {
    const refRows: string[][] = [
      ['Type', 'Name', 'Extra'],
      ...lookup.vessels.map((v) => ['Vessel', v.name, v.imoNumber ?? '']),
      ...lookup.categories.map((c) => ['Category', c.name, '']),
      ...lookup.vendors.map((v) => ['Vendor', v.name, '']),
      ...lookup.ports.slice(0, 500).map((p) => ['Port', p.name, p.country]),
    ];
    const refSheet = XLSX.utils.aoa_to_sheet(refRows);
    formatDataSheet(refSheet, [14, 36, 24], 1);
    XLSX.utils.book_append_sheet(wb, refSheet, 'Reference_Data');
  }

  XLSX.writeFile(wb, 'RFQ_Create_Template.xlsx');
}

/**
 * Legacy Field | Value vertical header parser (older templates).
 * Prefer extractFlexibleHeader for new tabular header sheets.
 */
export function parseHeaderSheet(sheet: XLSX.WorkSheet): Record<string, string> {
  const rows = XLSX.utils.sheet_to_json<(string | number | undefined)[]>(sheet, {
    header: 1,
    defval: '',
  });

  const result: Record<string, string> = {};

  for (const row of rows) {
    if (!Array.isArray(row) || row.length < 2) continue;
    const fieldLabel = String(row[0] ?? '')
      .trim()
      .replace(/\s*\*\s*$/, '');
    const value = String(row[1] ?? '').trim();
    if (!fieldLabel || fieldLabel.toLowerCase() === 'field') continue;
    if (fieldLabel.toLowerCase().includes('rfq header')) continue;
    if (fieldLabel.startsWith('←')) continue;
    if (!value) continue;

    const matched = RFQ_HEADER_FIELDS.find(
      (f) => normalizeKey(f.label) === normalizeKey(fieldLabel)
    );
    if (matched) {
      result[matched.key] = value;
      continue;
    }

    const aliasKey = resolveHeaderAliasKey(fieldLabel);
    if (aliasKey && !result[aliasKey]) {
      result[aliasKey] = value;
    }
  }

  return result;
}
